import * as React from 'react';
import { LuChevronDown, LuChevronUp, LuPen, LuPin, LuTrash2 } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { GUID, HouseholdType, TaskView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuthContext } from 'views/auth/Auth';
import { useDockState } from 'views/dock/dock-state';
import { useHouseholdMap } from 'views/household/household-queries';

import { useTaskView } from '../task-queries';
import { TaskFormType, toTaskFormValues } from './TaskForm';
import { TaskViewDetailsContent } from './TaskViewDetailsContent';

export interface TaskViewDetailsSheetProps {
    taskGuid: GUID<TaskView>;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onOpenDeleteDialog?: (taskGuid: GUID<TaskView>) => void;
}

const MAX_TITLE_LENGTH = 75;

export const TaskViewDetailsSheet = (props: TaskViewDetailsSheetProps) => {
    const { taskGuid } = props;
    const { currentPersonOrgGUID } = useAuthContext();
    const { data: task, isLoading } = useTaskView(taskGuid);

    const [showFullTitle, setShowFullTitle] = React.useState(false);
    const [commentDraft, setCommentDraft] = React.useState('');

    const navigate = useNavigate();
    const { addUpdateTaskDockItem } = useDockState();
    const householdMap = useHouseholdMap();

    const handlePin = React.useCallback(() => {
        if (!task) return;
        addUpdateTaskDockItem(
            taskGuid,
            task.householdGUID ?? undefined,
            toTaskFormValues(task) as TaskFormType,
            commentDraft,
            'view',
        );
        props.onOpenChange?.(false);
    }, [task, taskGuid, commentDraft, addUpdateTaskDockItem, props]);

    const handleEdit = React.useCallback(() => {
        if (!task) return;
        addUpdateTaskDockItem(
            taskGuid,
            task.householdGUID ?? undefined,
            toTaskFormValues(task) as TaskFormType,
            '',
            'edit',
        );
        props.onOpenChange?.(false);
    }, [task, taskGuid, addUpdateTaskDockItem, props]);

    const handleHouseholdClick = React.useCallback(() => {
        if (!task?.householdGUID) return;
        const household = householdMap.get(task.householdGUID);
        const householdType = household?.householdType ?? HouseholdType.CLIENT;
        const baseRoute = householdType === HouseholdType.PROSPECT ? 'prospect' : 'household';
        props.onOpenChange?.(false);
        addUpdateTaskDockItem(task.taskGUID, task.householdGUID, toTaskFormValues(task) as TaskFormType, '', 'view');
        navigate(`/${baseRoute}/${task.householdGUID}/tasks`);
    }, [task, householdMap, props, addUpdateTaskDockItem, navigate]);

    if (isLoading) {
        return (
            <Sheet open={props.open} onOpenChange={props.onOpenChange}>
                <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
                    <SheetHeader>
                        <SheetTitle>Loading task details...</SheetTitle>
                    </SheetHeader>
                </SheetContent>
            </Sheet>
        );
    }

    if (!task) {
        return (
            <Sheet open={props.open} onOpenChange={props.onOpenChange}>
                <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
                    <SheetHeader>
                        <SheetTitle>Task not found</SheetTitle>
                    </SheetHeader>
                </SheetContent>
            </Sheet>
        );
    }

    const taskTitle = task.title || 'Untitled Task';
    const isTitleTooLong = taskTitle.length > MAX_TITLE_LENGTH;
    const displayTitle =
        isTitleTooLong && !showFullTitle ? `${taskTitle.substring(0, MAX_TITLE_LENGTH)}...` : taskTitle;

    return (
        <Sheet open={props.open} onOpenChange={props.onOpenChange}>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
                <SheetHeader>
                    <SheetTitle>
                        <div className="flex items-center gap-2">
                            <span className="text-accent text-lg font-semibold">{displayTitle}</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Pin task"
                                className="shrink-0"
                                onClick={handlePin}
                                disabled={isLoading || !task}
                            >
                                <LuPin className="h-4 w-4" />
                            </Button>
                        </div>
                    </SheetTitle>
                    {isTitleTooLong && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowFullTitle(!showFullTitle)}
                            className="mt-1 flex h-auto items-center gap-1 py-1 text-xs"
                        >
                            {showFullTitle ? (
                                <>
                                    <LuChevronUp className="h-3 w-3" />
                                    <span>Show less</span>
                                </>
                            ) : (
                                <>
                                    <LuChevronDown className="h-3 w-3" />
                                    <span>Show full title</span>
                                </>
                            )}
                        </Button>
                    )}
                </SheetHeader>

                <div className="space-y-4 pt-4">
                    <TaskViewDetailsContent
                        task={task}
                        currentPersonOrgGUID={currentPersonOrgGUID}
                        commentDraft={commentDraft}
                        onCommentDraftChange={setCommentDraft}
                        onHouseholdClick={task.householdGUID ? handleHouseholdClick : undefined}
                    />

                    <TaskSheetActions
                        task={task}
                        onClose={() => props.onOpenChange?.(false)}
                        onOpenDeleteDialog={props.onOpenDeleteDialog}
                        onEdit={handleEdit}
                    />
                </div>
            </SheetContent>
        </Sheet>
    );
};

const TaskSheetActions = ({
    task,
    onClose,
    onOpenDeleteDialog,
    onEdit,
}: {
    task: TaskView;
    onClose: () => void;
    onOpenDeleteDialog?: (taskGuid: GUID<TaskView>) => void;
    onEdit?: () => void;
}) => {
    return (
        <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="flex items-center gap-2">
                Close
            </Button>
            {onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} className="flex items-center gap-2">
                    <LuPen className="h-4 w-4" />
                </Button>
            )}
            {onOpenDeleteDialog && (
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onOpenDeleteDialog(task.taskGUID)}
                    className="flex items-center gap-2"
                >
                    <LuTrash2 className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
};

export default TaskViewDetailsSheet;
